import React, { useEffect, useMemo, useState } from 'react';
import { Box, Cylinder } from '@react-three/drei';
import * as THREE from 'three';
import { getGrainBumpTexture } from '../utils/grainTexture';
import { PanelDepthTooltip, HoverEdgeFrame } from '../utils/edgeHover.jsx';

// PATAISYTA: buvo 0.08 — nedidelis, bet SĄMONINGAI matomas kontrastas tarp durelių ir
// karkaso, tam, kad durelių briaunos liktų įskaitomos net kai abiejų spalva sutampa
// (žr. [[project_carcass_color_match_2026_10]]). Vartotojas, pamatęs TOKĮ patį
// karkaso spalvos pataisymą gyvai, paprašė jį panaikinti visiškai: "Pašalinti
// visiškai (0%) — tiksliai ta pati spalva" — t.y. durelės ir karkasas turi būti
// TIKSLIAI ta pati spalva, be jokio atskyrimui skirto atspalvio, net rizikuojant,
// kad durelių briaunos taps mažiau įskaitomos be kontūrų/šešėlių pagalbos.
const DOOR_DARKEN_AMOUNT = 0;
// Realus tarpas tarp dviejų greta esančių durelių lapų (DoubleDoorGroup) — be jo
// dvi pusės susiliesdavo tiksliai per vidurį ir atrodė kaip vienas ištisinis
// paviršius, o ne du atskiri varstomi lapai.
const DOOR_GAP = 0.0015;
// Švelnus, PUSIAU SKAIDRUS šešėlio užpildas pačiame tarpe (ne solidi dažyta linija —
// buvo aiškiai atmesta anksčiau) — plotis tiksliai sutampa su pačiu tarpu (neplinta
// ant lapų paviršiaus), o skaidrumas leidžia tam, kas už jo, iš dalies prašviesti,
// todėl atrodo kaip natūralus šešėlis siaurame plyšyje, ne nupieštas brūkšnys.
const GROOVE_SHADOW_OPACITY = 0.55;
// /admin/bugs "3.0 Išorė. 'Ištraukti į priekį'. Su 'Rodyti dureles' blogai
// išbraižo." — šis šešėlis anksčiau stovėjo vos 0,0025m (2,5mm) priešais durų
// PAČIŲ priekinį paviršių, t.y. tik ~0,5mm priešais kaimyninės pertvaros/
// plokštės "Ištraukta į priekį" (fasado) plokštumą (žr. CabinetPanel/
// DividerDragHandle isFacade +0.006 poslinkį) — toks plonas tarpas sukeldavo
// Z-fighting artefaktą (juodą "įlinkimą") būtent ten, kur susikerta kelių
// durelių blokų šešėlio juostos su fasado plokštumoje stovinčia pertvara. Be
// durelių šito šešėlio scenoje iš viso nėra, tad konfliktas "išnykdavo" —
// klaidingai atrodydavo, kad problema susijusi su pačiu durelių rodymu.
// 0.006 (buvo 0.0025) užtikrina saugų ~3mm tarpą PRIEŠ fasado plokštumą.
const GROOVE_SHADOW_Z_OFFSET = 0.006;
// Stable default so OuterDoors doesn't get a fresh {} every render when the
// caller doesn't pass edgeGap (module-level, not recreated per render).
const EMPTY_EDGE_GAP = { top: 0, bottom: 0, left: 0, right: 0 };
const darkenColor = (color, amount = DOOR_DARKEN_AMOUNT) => {
    if (typeof color !== 'string' || !color.startsWith('#')) return color;
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return color;
    const num = parseInt(hex, 16);
    if (isNaN(num)) return color;
    const r = Math.max(0, Math.round(((num >> 16) & 255) * (1 - amount)));
    const g = Math.max(0, Math.round(((num >> 8) & 255) * (1 - amount)));
    const b = Math.max(0, Math.round((num & 255) * (1 - amount)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const makeFaceLoop = (w, h, z) => {
    const pts = new Float32Array([-w/2, -h/2, z, w/2, -h/2, z, w/2, h/2, z, -w/2, h/2, z]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    const loop = new THREE.LineLoop(geo, new THREE.LineBasicMaterial({ color: '#1a1a1a' }));
    // Purely a visual outline — without this, Line.raycast()'s large default hit
    // threshold lets clicks "hit" these edge lines instead of the divider/door
    // controls underneath, blocking selection whenever contours are shown
    // (see the identical fix on PanelEdges).
    loop.raycast = () => {};
    return loop;
};

const DoorEdgeLines = ({ width, height, thickness, posX, centerY, zPos, doorOption }) => {
    const obj = useMemo(() => {
        // SUVIENODINTA su SingleCabinet3D.jsx PanelEdges (buvo 0.006 čia vs 0.001 ten) —
        // vartotojas: "plokste yra istraukta i fasado plokstuma... kodel nesutampa konturu
        // linijos?" Kai durų ir į fasadą ištrauktos pertvaros/karkaso kontūrai žymi TĄ PATĮ
        // fizinį kraštą, 5mm skirtingas poslinkis sukurdavo matomą paralaksą žiūrint kampu
        // (statmenai nematoma, tad anksčiau atrodė nereikšminga). Abi linijos dabar TOS
        // PAČIOS spalvos (#1a1a1a) ir TO PAČIO poslinkio — joks z-fighting tarp jų pačių
        // nepastebimas (ta pati spalva laimi bet kuriuo atveju), o paralaksas išnyksta.
        const z = thickness / 2 + 0.001;
        if (doorOption === 3) {
            // Double doors: two half-width rectangles, each centred on its leaf
            const hw = width / 2;
            const group = new THREE.Group();
            const left = makeFaceLoop(hw, height, z);
            left.position.x = -hw / 2;   // left leaf centre
            group.add(left);
            const right = makeFaceLoop(hw, height, z);
            right.position.x = hw / 2;   // right leaf centre
            group.add(right);
            return group;
        }
        return makeFaceLoop(width, height, z);
    }, [width, height, thickness, doorOption]);

    useEffect(() => () => {
        const loops = obj.isGroup ? obj.children : [obj];
        loops.forEach(l => { l.geometry?.dispose(); l.material?.dispose(); });
    }, [obj]);

    return <primitive object={obj} position={[posX, centerY, zPos]} />;
};

// Shared chrome material — module-level to avoid per-render allocations (#12)
const CHROME_MAT = new THREE.MeshStandardMaterial({ color: '#d8d8d8', roughness: 0.15, metalness: 0.92 });

// ── #12 renderHandleByType ────────────────────────────────────────────────────
// Renders one of 5 handle styles based on handleType:
//   0 = none, 1 = standard bar (default), 2 = knob, 3 = J-profile, 4 = square profile
const renderHandleByType = (handleType, doorH, doorW, thickness, pos, isVertical = true) => {
    if (handleType === 0) return null;
    const type = handleType || 1;
    const len = isVertical ? Math.min(doorH * 0.15, 0.25) : Math.min(doorW * 0.15, 0.25);

    if (type === 2) {
        // Knob — spherical knob centred on the door face
        return (
            <group position={pos}>
                <mesh position={[0, 0, thickness / 2 + 0.018]}>
                    <sphereGeometry args={[0.018, 16, 12]} />
                    <primitive object={CHROME_MAT} attach="material" />
                </mesh>
                <mesh position={[0, 0, thickness / 2 + 0.006]}>
                    <cylinderGeometry args={[0.006, 0.006, 0.012, 10]} />
                    <primitive object={CHROME_MAT} attach="material" />
                </mesh>
            </group>
        );
    }

    if (type === 3) {
        // J-profile — longer bar with visible mounting brackets at both ends
        const jLen = Math.min(len * 1.5, 0.35);
        return (
            <group position={pos}>
                <Cylinder args={[0.010, 0.010, jLen, 12]}
                    rotation={[0, 0, isVertical ? 0 : Math.PI / 2]}
                    position={[0, 0, thickness / 2 + 0.025]}>
                    <primitive object={CHROME_MAT} attach="material" />
                </Cylinder>
                {[1, -1].map(s => (
                    <Cylinder key={s} args={[0.007, 0.007, 0.025, 10]}
                        rotation={[Math.PI / 2, 0, 0]}
                        position={isVertical
                            ? [0, s * (jLen / 2 - 0.015), thickness / 2 + 0.0125]
                            : [s * (jLen / 2 - 0.015), 0, thickness / 2 + 0.0125]}>
                        <primitive object={CHROME_MAT} attach="material" />
                    </Cylinder>
                ))}
            </group>
        );
    }

    if (type === 4) {
        // Square/flat bar profile — rectangular cross-section with end caps
        const sqLen = Math.min(len * 1.2, 0.32);
        const sqD = 0.012;
        const sqH = 0.008;
        return (
            <group position={pos}>
                <Box args={[isVertical ? sqH : sqLen, isVertical ? sqLen : sqH, sqD]}
                    position={[0, 0, thickness / 2 + sqD / 2 + 0.01]}>
                    <primitive object={CHROME_MAT} attach="material" />
                </Box>
                {[1, -1].map(s => (
                    <Box key={s} args={[sqH, sqH, sqD * 0.5]}
                        position={isVertical
                            ? [0, s * (sqLen / 2 - sqH / 2), thickness / 2 + 0.006]
                            : [s * (sqLen / 2 - sqH / 2), 0, thickness / 2 + 0.006]}>
                        <primitive object={CHROME_MAT} attach="material" />
                    </Box>
                ))}
            </group>
        );
    }

    // Type 1 (default): standard cylindrical bar handle
    return (
        <group position={pos}>
            <Cylinder args={[0.008, 0.008, len, 16]}
                rotation={[0, 0, isVertical ? 0 : Math.PI / 2]}
                position={[0, 0, 0.015]}>
                <primitive object={CHROME_MAT} attach="material" />
            </Cylinder>
            <Cylinder args={[0.006, 0.006, 0.015, 16]}
                rotation={[Math.PI / 2, 0, 0]}
                position={isVertical ? [0, len / 2 - 0.02, 0.0075] : [len / 2 - 0.02, 0, 0.0075]}>
                <primitive object={CHROME_MAT} attach="material" />
            </Cylinder>
            <Cylinder args={[0.006, 0.006, 0.015, 16]}
                rotation={[Math.PI / 2, 0, 0]}
                position={isVertical ? [0, -len / 2 + 0.02, 0.0075] : [-len / 2 + 0.02, 0, 0.0075]}>
                <primitive object={CHROME_MAT} attach="material" />
            </Cylinder>
        </group>
    );
};

// ── DoorFaceMaterial ─────────────────────────────────────────────────────────
// #3 glass / #4 PBR texture — shared helper so all door panels use same logic
const DoorFaceMaterial = ({ isGlass, plateColor, plateTexture }) => {
    if (isGlass) {
        return (
            <meshPhysicalMaterial
                color="#c8e8ff"
                transparent
                opacity={0.18}
                roughness={0.04}
                metalness={0.02}
                transmission={0.88}
                thickness={0.008}
                ior={1.5}
                side={THREE.DoubleSide}
            />
        );
    }
    // roughness/metalness anksčiau nebuvo nurodyti — numatytieji three.js meshStandardMaterial
    // (roughness=1, metalness=0) yra visiškai matinis, plokščias paviršius, kuris visai
    // neatspindi aplinkos (Environment IBL), todėl durelės atrodydavo "negyvos"/plastikinės.
    // Šios reikšmės imituoja realų melamino/laminuoto paviršiaus blizgesį.
    // `color` multiplina tekstūrą — kai tekstūros nėra, naudojamas plokščias plateColor
    // (šviesiai pilkas atsarginis atspalvis, žr. currentColor SingleCabinet3D.jsx).
    // Kai tekstūra įkelta, naudojame beveik baltą (su tuo pačiu siūlės atskyrimo
    // patamsinimu — žr. DOOR_DARKEN_AMOUNT pastabą viršuje). bumpMap tik kai NĖRA
    // realios dekoro tekstūros — grubumas čia tik tam, kad vienspalvis fonas
    // neatrodytų kaip plastikas.
    // polygonOffset: karkaso plokštuma (nefasadinė, bordersDoor) dabar atsitraukia
    // per PILNĄ wallThickness nuo durelių (žr. doorZPosition) — patogus ~1.8cm+ tarpas,
    // jokio z-fighting rizikos ten. Vienintelis likęs TIKRAS lygiųjų atvejis: sekcija,
    // kurios skydas/pertvara pažymėta "Ištraukta į priekį" (isFacade), stovi LYGIAI tame
    // pačiame B gylyje kaip ir durelė (abi TIKSLIOS diagnostikos dėlei, žr.
    // PanelDepthTooltip) — be offset'o, GPU laimėtoją rinktųsi atsitiktinai (flickering).
    // Neigiamas offset visada pastumia šį paviršių truputį arčiau kameros gylio teste;
    // isFacade skydo/pertvaros medžiaga (SingleCabinet3D.jsx) gauna DAR neigiamesnį
    // (-8/-8 > šio -4/-4), tad JI laimi ir lieka matoma, kai abi sutampa.
    const faceColor = plateTexture ? darkenColor('#ffffff') : plateColor;
    return (
        <meshStandardMaterial
            color={faceColor} map={plateTexture || undefined}
            bumpMap={plateTexture ? undefined : getGrainBumpTexture()} bumpScale={plateTexture ? 0 : 0.0018}
            roughness={0.3} metalness={0} envMapIntensity={0}
            // metalness 0.07→0, envMapIntensity 1.35→0.2 — vartotojas pastebėjo, kad
            // žiūrint į baldą iš platesnio/centruoto kameros kampo (ne tiesiai/priartinus),
            // durelių KRAŠTUOSE atsiranda tamsesnė sritis, kuri dingsta priartinus ar
            // žiūrint tiesiai — klasikinis Fresnel/aplinkos atspindžio (Environment IBL,
            // žr. Builder3D.jsx <Environment preset="apartment">) požymis esant NET
            // nedideliam metalness: plokščio paviršiaus kraštuose atspindžio kampas labiau
            // skiriasi nuo centro, tad atsispindi kitokia (čia: tamsesnė) aplinkos dalis.
            // metalness=0 pašalina metalo PBR šaką beveik visiškai (lieka tik nedidelis
            // fiksuotas dielektrinis atspindys), tad paviršius vienodas iš bet kurio kampo.
            // Bendra ryškumo "grindis" (0.3) PAČIA durelių spalva — žr. identišką
            // SingleCabinet3D.jsx CabinetPanel komentarą: durelių ŠONINĖ briauna (kur ji
            // ribojasi su karkaso siena status kampu) anksčiau neturėjo JOKIO tokio
            // pakėlimo (skirtingai nuo karkaso, kuriam jis jau pridėtas), tad liko matoma
            // kaip tamsesnė juosta net po to, kai DOOR_DARKEN_AMOUNT nulintas — vartotojas:
            // "kodel tarp dureliu ir soniniu plokstes matoma kita spalva?", patvirtinta
            // gyvai, kad ta juosta yra TAME PAČIAME gylyje, kaip ir pati durelė (ne
            // geometrinis tarpas).
            emissive={faceColor} emissiveIntensity={0.3}
            // -4/-4 → -1/-1 (žr. porinį -8→-2 SingleCabinet3D.jsx/UnifiedTopPanel.jsx isFacade
            // pakeitimą): šis poslinkis sprendžia TIK koplanarumą su isFacade siena/pertvara, bet
            // GPU gylio testui jis nėra selektyvus — veikia šios plokštės gylio palyginimą SU
            // VISKUO aplink, ne vien su ja. -4 buvo gerokai daugiau nei reikia paprastai lygiajai
            // išspręsti (užtenka ±1), tad "šalutinis poveikis" kaimyninėms (offset'o neturinčioms)
            // riboms — pvz. lentynai — tapo pastebimas kaip tarpas/persišvietimas, nors koordinatės
            // (vertex pozicijos) sutampa tiksliai. -1 išlaiko TĄ PATĮ santykį su -2 (vis dar
            // mažiau neigiamas, tad isFacade plokštė vis tiek laimi), bet su mažesniu spinduliu.
            polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1}
        />
    );
};

// ── SwingDoorPanel ────────────────────────────────────────────────────────────
const SwingDoorPanel = ({ posX, centerY, width, height, thickness, zPos, plateColor, plateTexture, doorOption, hingeOnLeft, doorHeight, doorWidth, handleType, isGlass }) => {
    const pivotX = hingeOnLeft ? posX - width / 2 : posX + width / 2;
    const doorOffsetX = hingeOnLeft ? width / 2 : -width / 2;

    return (
        <group position={[pivotX, centerY, zPos]}>
            <Box args={[width, height, thickness]} position={[doorOffsetX, 0, 0]} castShadow receiveShadow>
                <DoorFaceMaterial isGlass={isGlass} plateColor={plateColor} plateTexture={plateTexture} />
            </Box>
            {doorOption === 1 && renderHandleByType(handleType, doorHeight, doorWidth, thickness, [doorOffsetX + width / 2 - 0.08, 0, thickness / 2 + 0.01], true)}
            {doorOption === 2 && renderHandleByType(handleType, doorHeight, doorWidth, thickness, [doorOffsetX - width / 2 + 0.08, 0, thickness / 2 + 0.01], true)}
        </group>
    );
};

// ── HalfDoorLeaf ─────────────────────────────────────────────────────────────
const HalfDoorLeaf = ({ pivotX, centerY, halfWidth, height, thickness, zPos, plateColor, plateTexture, isLeftHalf, doorHeight, doorWidth, handleType, isGlass }) => {
    const doorOffsetX = isLeftHalf ? halfWidth / 2 : -halfWidth / 2;
    const handleOffsetX = isLeftHalf ? halfWidth / 2 - 0.05 : -halfWidth / 2 + 0.05;
    // Only the visible face pulls back from the shared centerline by half of DOOR_GAP —
    // the handle and the leaf's own pivot/hinge math above stay on the full nominal
    // halfWidth, so this is purely cosmetic (no effect on click hitboxes/positions).
    const visibleWidth = Math.max(0.01, halfWidth - DOOR_GAP / 2);
    const visibleOffsetX = doorOffsetX + (isLeftHalf ? -DOOR_GAP / 4 : DOOR_GAP / 4);

    return (
        <group position={[pivotX, centerY, zPos]}>
            <Box args={[visibleWidth, height, thickness]} position={[visibleOffsetX, 0, 0]} castShadow receiveShadow>
                <DoorFaceMaterial isGlass={isGlass} plateColor={plateColor} plateTexture={plateTexture} />
            </Box>
            {renderHandleByType(handleType, doorHeight, doorWidth / 2, thickness, [doorOffsetX + handleOffsetX, 0, thickness / 2 + 0.01], true)}
        </group>
    );
};

// ── DoubleDoorGroup ───────────────────────────────────────────────────────────
const DoubleDoorGroup = ({ posX, centerY, doorWidth, doorHeight, thickness, zPos, plateColor, plateTexture, handleType, isGlass }) => {
    return (
        <group>
            <HalfDoorLeaf
                pivotX={posX - doorWidth / 2} centerY={centerY}
                halfWidth={doorWidth / 2} height={doorHeight} thickness={thickness}
                zPos={zPos} plateColor={plateColor} plateTexture={plateTexture}
                isLeftHalf={true}
                doorHeight={doorHeight} doorWidth={doorWidth}
                handleType={handleType} isGlass={isGlass}
            />
            <HalfDoorLeaf
                pivotX={posX + doorWidth / 2} centerY={centerY}
                halfWidth={doorWidth / 2} height={doorHeight} thickness={thickness}
                zPos={zPos} plateColor={plateColor} plateTexture={plateTexture}
                isLeftHalf={false}
                doorHeight={doorHeight} doorWidth={doorWidth}
                handleType={handleType} isGlass={isGlass}
            />
            {/* Švelnus, pusiau skaidrus šešėlis pačiame tarpe — plotis tiksliai = DOOR_GAP
                (nesiplečia ant lapų paviršiaus), skaidrumas (GROOVE_SHADOW_OPACITY) leidžia
                atrodyti kaip natūraliam šešėliui, ne dažytai linijai. z pozicija TURI būti
                prieš durų priekinį paviršių (thickness/2 + poslinkis) — testuota, kad
                pastačius ties/už centro tarpas praktiškai nesimato (durų lapų kraštai
                geometriškai užstoja bet ką giliau, o ne tik plokščią kampą). */}
            <Box position={[posX, centerY, zPos + thickness / 2 + GROOVE_SHADOW_Z_OFFSET]} args={[DOOR_GAP, doorHeight, 0.0015]}>
                <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0} transparent opacity={GROOVE_SHADOW_OPACITY} depthWrite={false} />
            </Box>
        </group>
    );
};

// ── FlipDoorPanel ─────────────────────────────────────────────────────────────
const FlipDoorPanel = ({ posX, centerY, width, height, thickness, zPos, plateColor, plateTexture, flipUp, doorHeight, doorWidth, handleType, isGlass }) => {
    const pivotY = flipUp ? centerY - height / 2 : centerY + height / 2;
    const doorOffsetY = flipUp ? height / 2 : -height / 2;

    return (
        <group position={[posX, pivotY, zPos]}>
            <Box args={[width, height, thickness]} position={[0, doorOffsetY, 0]} castShadow receiveShadow>
                <DoorFaceMaterial isGlass={isGlass} plateColor={plateColor} plateTexture={plateTexture} />
            </Box>
            {renderHandleByType(handleType, doorHeight, doorWidth, thickness,
                [0, flipUp ? doorOffsetY + height / 2 - 0.1 : doorOffsetY - height / 2 + 0.1, thickness / 2 + 0.01], false)}
        </group>
    );
};

// ── DrawerPanel ───────────────────────────────────────────────────────────────
const DrawerPanel = ({ posX, centerY, width, height, thickness, zPos, plateColor, plateTexture, doorHeight, doorWidth, handleType, isGlass }) => {
    return (
        <group position={[posX, centerY, zPos]}>
            <Box args={[width, height, thickness]} castShadow receiveShadow>
                <DoorFaceMaterial isGlass={isGlass} plateColor={plateColor} plateTexture={plateTexture} />
            </Box>
            {renderHandleByType(handleType, doorHeight, doorWidth, thickness, [0, 0, thickness / 2 + 0.01], false)}
        </group>
    );
};

// ── OuterDoors ────────────────────────────────────────────────────────────────
const OuterDoors = ({
    posX, doorCenterY, cabinet, wallThickness, doorHeight, doorOption,
    handleType, groupId, isTopSection, onClick, isSelected,
    selectedPlate, activeStep, fallbackColor, showEdges = false,
    edgeGap = EMPTY_EDGE_GAP
}) => {
    // /admin/bugs "Gylio tooltip reikia rodyti... ant baldo durelių" — ta pati
    // PanelDepthTooltip, kaip visos kitos plokštės (žr. edgeHover.jsx), rodoma
    // užvedus pelę ant ŠIO (jau esamo) permatomo paspaudimo Box'o, kuris jau
    // apima VISĄ durelių/stalčiaus plotą, nepriklausomai nuo durelių tipo
    // (varstomos/dvigubos/pakeliamos/stalčius). TYČIA nepriklauso nuo jokios
    // activeStep/selectable sąlygos — informacinis elementas, ne veiksmas.
    const [depthHovered, setDepthHovered] = useState(false);
    // Durelės BUVO vienintelis dažnai spaudžiamas paviršius VISOJE programoje be jokio
    // briaunos-only hover/click apribojimo ir be žalio rėmelio atsako — kitaip nei
    // SingleCabinet3D.jsx CabinetPanel/UnifiedBase.jsx FrontTrimBox ir t.t. Pirmas
    // bandymas: suvienodinti su likusia programa (briaunos-only click/hover, kaip
    // CabinetPanel). Vartotojas iš pradžių tai patvirtino abstrakčiai (AskUserQuestion),
    // bet GYVAI išbandęs pranešė: "Isore puslapyje... spaudziu ant dureliu neatsiranda
    // menu kuris buvo anksciau" — briaunos-only apribojimas prasmingas TEN, kur kelios
    // KAIMYNINĖS plokštės gali persidengti tame pačiame regione (reikia atskirti,
    // KURIĄ tiksliai taikinys) — bet durelės, skirtingai nei karkaso plokštės, YRA
    // vienintelis, priekiausias, savarankiškai taikinamas paviršius, be tokio
    // persidengimo poreikio (TA PATI priežastis, dėl kurios UnifiedTopPanel.jsx TopBox
    // irgi liko VISO paviršiaus, ne tik briaunos — "vartotojas natūraliai spaudžia
    // VIDURĮ"). Durelės GRĄŽINTOS prie VISO paviršiaus click/hover (kaip buvo iki
    // suvienodinimo bandymo) — apribojimas TIK 'Išorė' žingsniui liko nereikalingas,
    // tad pašalintas visai šiam elementui.
    const [edgeHovered, setEdgeHovered] = useState(false);
    const isIsore = activeStep === 'Išorė';
    // ClickUp "3.0 Išorė. Durelių matmenys... Durelės turi būti platesnės ir
    // aukštesnės" — PATAISYTA (2 raundai, abu rasti vartotojo screenshot'ais): 1 raunde
    // paaiškėjo, kad šis atėmimas tiesiog panaikindavo SingleCabinet3D.jsx call'o
    // pridėtą +wallThickness*2 "kompensaciją" (grynas no-op apvalinimo ratas, durelės
    // niekad neuždengdavo karkaso). 2 raunde (dviguba linija prie VIDINIO daliklio)
    // paaiškėjo, kad TA besąlygiška +wallThickness*2 "kompensacija" PATI buvo klaidinga
    // kryptis — ji teisingai veikė TIK kai ABU kraštai buvo tikri išoriniai, bet per
    // daug pridėdavo VIDINIAM (daliklio) kraštui, kuris jau pats teisingai gauna savo
    // wallThickness/2 iš innerDoorFacadeExtend. Galutinis sprendimas: `cabinet.width`
    // DABAR jau yra TIKSLI, galutinė reikšmė (kiekvienas bExtend/uExtend kraštas
    // SingleCabinet3D.jsx pats neša teisingą savo wallThickness/wallThickness/2/0
    // indėlį, žr. ten komentarą) — čia JOKS papildomas atėmimas/pridėjimas nebereikalingas.
    const doorWidth = cabinet.width;
    const doorThickness = wallThickness;
    // Two independent door groups from different structural sections (e.g. one
    // stacked above another, or side by side) each extend halfway into the
    // divider between them, meeting exactly at its centerline — with zero visible
    // separation. `edgeGap` (computed by the caller from whether each edge sits
    // at the cabinet's true outer boundary or an internal divider) pulls the
    // VISIBLE face back from internal edges only, mirroring the DOOR_GAP treatment
    // DoubleDoorGroup already does for its own two leaves. The click hitbox below
    // stays at the full nominal size, so the gap itself is still clickable.
    const visibleDoorWidth = Math.max(0.02, doorWidth - edgeGap.left - edgeGap.right);
    const visibleDoorHeight = Math.max(0.02, doorHeight - edgeGap.top - edgeGap.bottom);
    const visiblePosX = posX + (edgeGap.left - edgeGap.right) / 2;
    const visibleCenterY = doorCenterY + (edgeGap.bottom - edgeGap.top) / 2;
    // /admin/bugs "plokščių/durelių gylio diagnostika" — vartotojas: "maksimalus gylis
    // visais atvejais yra baldo gylis" — durelių PRIEKINIS paviršius (doorZPosition +
    // doorThickness/2) dabar TIKSLIAI = cabinet.depth, jokios papildomos paraštės (anksčiau
    // čia buvęs +0.003 stumdavo dureles 3mm UŽ baldo nominalaus gylio ribos — prieštarauja
    // taisyklei, kad fasado/durelių paviršius niekada neviršija baldo gylio, pvz. statant į
    // nišą). Z-fighting su ties TUO PAČIU gyliu atsidūrusia "Ištraukta į priekį" karkaso
    // plokšte sprendžiamas NE šiuo poslinkiu, o polygonOffset (žr. DoorFaceMaterial) —
    // pozicija lieka matematiškai tiksli diagnostikai (PanelDepthTooltip).
    const doorZPosition = cabinet.depth - doorThickness / 2;
    const rawPlateColor = selectedPlate?.color;
    const plateColor = darkenColor((rawPlateColor && typeof rawPlateColor === 'string' && (rawPlateColor.startsWith('#') || rawPlateColor.startsWith('rgb'))) ? rawPlateColor : (fallbackColor || '#d6d6d6'));

    // #3 glass detection — checks panelType === 3, isGlass flag, or plate name
    const isGlass = !!(selectedPlate?.isGlass || selectedPlate?.panelType === 3 ||
        selectedPlate?.name?.toLowerCase?.().includes('stikl') ||
        selectedPlate?.name?.toLowerCase?.().includes('glass'));

    // Durelėms tekstūros nebetaikomos — visada rodoma paprasta, vienoda spalva
    // (plateColor), nepriklausomai nuo pasirinktos plokštės (žr. vartotojo
    // "Baldo plokstems nereikia taikyti teksturu, tegul buna visada tokios
    // pacios spalvos" — ta pati pataisa, kaip SingleCabinet3D.jsx). DoorFaceMaterial
    // jau turi tvarkingą atsarginį kelią plateTexture===null atveju (paprasta
    // spalva + procedūrinis bumpMap paviršiaus reljefui), tad realaus tekstūros
    // paveikslėlio čia tiesiog niekada nebekeliame.
    const doorTex = null;

    const hingeOnLeft = doorOption === 1;
    const hType = handleType || 1;

    const commonProps = {
        plateColor, plateTexture: doorTex,
        handleType: hType, isGlass,
        doorHeight, doorWidth,
    };

    const renderDoorVisual = () => {
        if (doorOption === 3) {
            return (
                <DoubleDoorGroup
                    posX={visiblePosX} centerY={visibleCenterY}
                    doorWidth={visibleDoorWidth} doorHeight={visibleDoorHeight}
                    thickness={doorThickness} zPos={doorZPosition}
                    {...commonProps}
                />
            );
        }
        if (doorOption === 4) {
            return (
                <FlipDoorPanel
                    posX={visiblePosX} centerY={visibleCenterY}
                    width={visibleDoorWidth} height={visibleDoorHeight} thickness={doorThickness}
                    zPos={doorZPosition} flipUp={true}
                    {...commonProps}
                />
            );
        }
        if (doorOption === 5) {
            return (
                <FlipDoorPanel
                    posX={visiblePosX} centerY={visibleCenterY}
                    width={visibleDoorWidth} height={visibleDoorHeight} thickness={doorThickness}
                    zPos={doorZPosition} flipUp={false}
                    {...commonProps}
                />
            );
        }
        if (doorOption === 6) {
            return (
                <DrawerPanel
                    posX={visiblePosX} centerY={visibleCenterY}
                    width={visibleDoorWidth} height={visibleDoorHeight} thickness={doorThickness}
                    zPos={doorZPosition}
                    {...commonProps}
                />
            );
        }
        return (
            <SwingDoorPanel
                posX={visiblePosX} centerY={visibleCenterY}
                width={visibleDoorWidth} height={visibleDoorHeight} thickness={doorThickness}
                zPos={doorZPosition} doorOption={doorOption} hingeOnLeft={hingeOnLeft}
                {...commonProps}
            />
        );
    };

    return (
        <>
            {/* Transparent clickable overlay — stopPropagation VISADA pirmoje eilutėje
                (žr. SingleCabinet3D.jsx CabinetPanel komentarą apie "paspaudimas iškrenta
                iki FurnitureGroup" klaidą) — šis sluoksnis jau ir taip stop'indavo
                besąlygiškai, tad ta klaida čia nebuvo, tik trūko pačios briaunos-only
                patikros Išorė žingsniui. */}
            <Box
                position={[posX, doorCenterY, doorZPosition + 0.02]}
                args={[doorWidth, doorHeight, doorThickness]}
                onClick={(e) => { e.stopPropagation(); onClick(e); }}
                onPointerOver={(e) => {
                    e.stopPropagation(); setDepthHovered(true);
                    if (!isIsore) return;
                    setEdgeHovered(true);
                    document.body.style.cursor = 'pointer';
                }}
                onPointerOut={() => { setDepthHovered(false); setEdgeHovered(false); if (isIsore) document.body.style.cursor = ''; }}
            >
                <meshStandardMaterial transparent opacity={0} depthWrite={false} />
            </Box>
            {/* Žalias briaunos rėmelis — TA PATI vizualinė kalba, kaip visos kitos
                plokštės programoje (žr. edgeHover.jsx HoverEdgeFrame); anksčiau durelės
                vienintelės neturėjo jokio hover atsako, o pažymėjimą rodė VISO paviršiaus
                mėlynu atspalviu — pakeista rėmeliu, kad atitiktų likusią programą.
                KLAIDA (vartotojo screenshot — žalias kvadratas šone/po grindimis): kitaip
                nei CabinetPanel/TopBox (visi savo elementai, ĮSKAITANT HoverEdgeFrame,
                stovi VIENO apgaubiančio <group position={...}> viduje) ar DividerDragHandle
                (HoverEdgeFrame atskirai apgaubtas <group position={wallPos}>), OuterDoors
                kiekvienam elementui poziciją nurodo ATSKIRAI (žr. Box/PanelDepthTooltip
                aukščiau/žemiau) — HoverEdgeFrame PATS savo pozicijos neturi, tad be šio
                apgaubimo jis piešdavosi protėvio group'o (0,0,0) atžvilgiu, t.y. VISIŠKAI
                NE ten, kur durelės realiai stovi. */}
            {/* ClickUp "3.0 Išorė. Durelių matmenys/pozicija. Matmenų išvestinėje
                matmenys blogi" — rėmelis (ir iš jo išvedamas matmuo) anksčiau naudojo
                `doorWidth`/`doorHeight` — PILNĄ, dar BE edgeGap sumažinimo, "lizdo"
                matmenį — ne TIKRAI matomą, sumažintą `visibleDoorWidth`/`visibleDoorHeight`
                (žr. šių dviejų apibrėžimą aukščiau — tas pats kintamasis, kurį naudoja
                visos renderDoorVisual() durelių variacijos). Patvirtinta vartotojo
                "Plokščių koordinatės" įrankiu: rėmelis/matmuo rodė 58,2 cm, nors reali,
                scenos geometrijoje atvaizduota durelė yra 58,1 cm (58,2 - edgeGap,
                besiribojanti su vidiniu dalikliu). Dabar rėmelis atitinka TIKRĄ,
                sumažintą durelių dydį, su kuriuo jis vizualiai sutampa ekrane. */}
            <group position={[posX, doorCenterY, doorZPosition]}>
                <HoverEdgeFrame width={visibleDoorWidth} height={visibleDoorHeight} depth={doorThickness} visible={isIsore && (edgeHovered || isSelected)} />
            </group>
            {/* depth = atstumas nuo galinės baldo sienos (Z=0) iki durelių PRIEKINIO
                paviršiaus — ta pati "nuo galo" semantika kaip CabinetPanel/Divider
                tooltip'uose, žr. vartotojo B/B-D diagnostikos taisyklę aukščiau. */}
            <PanelDepthTooltip depth={doorZPosition + doorThickness / 2} position={[posX, doorCenterY, doorZPosition + doorThickness / 2 + 0.02]} visible={depthHovered} />

            {renderDoorVisual()}

            {/* Švelnus, pusiau skaidrus šešėlis ten, kur šis durų blokas ribojasi su KITU
                nepriklausomu durų bloku (viena sekcija virš/šalia kitos) — ta pati logika
                kaip DoubleDoorGroup (žr. GROOVE_SHADOW_OPACITY pastabą). Centruota ant
                TIKROS sekcijų ribos (posX±doorWidth/2 / doorCenterY±doorHeight/2), plotis —
                tik šio bloko paties edgeGap dalis; kaimyninis blokas piešia savąją pusę. */}
            {edgeGap.left > 0 && (
                <Box position={[posX - doorWidth / 2 + edgeGap.left / 2, doorCenterY, doorZPosition + doorThickness / 2 + GROOVE_SHADOW_Z_OFFSET]} args={[edgeGap.left, doorHeight, 0.0015]}>
                    <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0} transparent opacity={GROOVE_SHADOW_OPACITY} depthWrite={false} />
                </Box>
            )}
            {edgeGap.right > 0 && (
                <Box position={[posX + doorWidth / 2 - edgeGap.right / 2, doorCenterY, doorZPosition + doorThickness / 2 + GROOVE_SHADOW_Z_OFFSET]} args={[edgeGap.right, doorHeight, 0.0015]}>
                    <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0} transparent opacity={GROOVE_SHADOW_OPACITY} depthWrite={false} />
                </Box>
            )}
            {edgeGap.bottom > 0 && (
                <Box position={[posX, doorCenterY - doorHeight / 2 + edgeGap.bottom / 2, doorZPosition + doorThickness / 2 + GROOVE_SHADOW_Z_OFFSET]} args={[doorWidth, edgeGap.bottom, 0.0015]}>
                    <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0} transparent opacity={GROOVE_SHADOW_OPACITY} depthWrite={false} />
                </Box>
            )}
            {edgeGap.top > 0 && (
                <Box position={[posX, doorCenterY + doorHeight / 2 - edgeGap.top / 2, doorZPosition + doorThickness / 2 + GROOVE_SHADOW_Z_OFFSET]} args={[doorWidth, edgeGap.top, 0.0015]}>
                    <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0} transparent opacity={GROOVE_SHADOW_OPACITY} depthWrite={false} />
                </Box>
            )}

            {showEdges && (
                <DoorEdgeLines
                    width={visibleDoorWidth}
                    height={visibleDoorHeight}
                    thickness={doorThickness}
                    posX={visiblePosX}
                    centerY={visibleCenterY}
                    zPos={doorZPosition}
                    doorOption={doorOption}
                />
            )}
        </>
    );
};

export default OuterDoors;
